/**
 * Server-side control for live A/B tests.
 *
 * The admin Analytics → Experiments tab writes one row per experiment through
 * the set_experiment_control RPC. Every browser reads those rows once at boot
 * and then assignExperimentVariant consults them before it rolls the die:
 *
 *   forced_variant set   -> that variant, for everyone
 *   forced_variant null  -> normal split, but only rollout_pct% of browsers join it
 *
 * A browser outside the rollout sees the experiment's first variant (the current
 * design) and keeps seeing it, so ramping traffic cannot reshuffle people who
 * were already in the test.
 *
 * Failure is never fatal: a slow or failed read keeps the last cached value, and
 * with no value at all the test behaves exactly as it did before this file.
 */

import { createClient } from "@supabase/supabase-js";

const CACHE_KEY = "22n_experiment_controls_v1";
const REFRESH_MS = 60 * 1000;

let controls = new Map();
let loadedAt = 0;
let inflight = null;
let hydrated = false;
let loaded = false;

// A dedicated read-only client, created on first use. Keeping it out of the
// module graph of supabaseClient.js means the boot read starts immediately
// instead of waiting for an extra chunk, which is the difference between the
// rollout being honoured and a first-time visitor slipping past it.
let publicClient = null;
const readClient = () => {
  if (publicClient) return publicClient;
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!url || !key) return null;
  publicClient = createClient(url, key, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
  return publicClient;
};

const isVariantId = (value) => typeof value === "string" && /^[a-z0-9_]{2,40}$/.test(value);
const toPct = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : 100;
};

const normalize = (row) => ({
  forcedVariant: isVariantId(row?.forced_variant) ? row.forced_variant : null,
  rolloutPct: toPct(row?.rollout_pct),
  forcedAt: row?.forced_at || null,
});

const readCache = () => {
  if (hydrated) return;
  hydrated = true;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const rows = Array.isArray(parsed?.rows) ? parsed.rows : [];
    rows.forEach((row) => {
      if (row?.experiment_id) controls.set(String(row.experiment_id), normalize(row));
    });
    // A cached read is good enough to honour a rollout on the very first paint.
    if (rows.length > 0) loaded = true;
  } catch {
    // Unreadable cache is the same as no cache.
  }
};

const writeCache = () => {
  try {
    const rows = [...controls.entries()].map(([experiment_id, value]) => ({
      experiment_id,
      forced_variant: value.forcedVariant,
      rollout_pct: value.rolloutPct,
      forced_at: value.forcedAt,
    }));
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: loadedAt, rows }));
  } catch {
    // Private mode / blocked storage: the in-memory map still works this session.
  }
};

/** Raw control for one experiment, or null when it was never configured. */
export function getExperimentControl(experimentId) {
  readCache();
  return controls.get(String(experimentId)) || null;
}

/** The forced variant, but only while it is still one of this experiment's variants. */
export function getForcedVariant(experimentId, variants = []) {
  const control = getExperimentControl(experimentId);
  if (!control?.forcedVariant) return null;
  return variants.includes(control.forcedVariant) ? control.forcedVariant : null;
}

/** Share of browsers allowed into the split. 100 unless an admin lowered it. */
export function getRolloutPct(experimentId) {
  const control = getExperimentControl(experimentId);
  return control ? control.rolloutPct : 100;
}

/** True once the switches are known, from the cache or from the server. */
export function areExperimentControlsLoaded() {
  readCache();
  return loaded;
}

/**
 * Fetch the controls. Called once from index.js and again when the admin tab
 * wants fresh values. Never throws: a failed read keeps what it had, and it
 * still marks the switches as known so the test does not stall on a network
 * error.
 */
export async function loadExperimentControls({ force = false, client } = {}) {
  readCache();
  if (!force && Date.now() - loadedAt < REFRESH_MS) return controls;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const supabaseClient = client || readClient();
      if (!supabaseClient) return controls;
      const { data, error } = await supabaseClient.rpc("get_experiment_controls");
      if (error) throw error;
      const rows = Array.isArray(data) ? data : [];
      const next = new Map();
      rows.forEach((row) => {
        if (row?.experiment_id) next.set(String(row.experiment_id), normalize(row));
      });
      controls = next;
      loadedAt = Date.now();
      writeCache();
      return controls;
    } catch {
      return controls;
    } finally {
      loaded = true;
      inflight = null;
    }
  })();

  return inflight;
}

/** Applied by the admin tab straight after a successful save. */
export function applyExperimentControl(row) {
  readCache();
  if (!row?.experiment_id) return;
  controls.set(String(row.experiment_id), normalize(row));
  loadedAt = Date.now();
  writeCache();
}

/** Test-only reset of the module cache. */
export function resetExperimentControlsForTests() {
  controls = new Map();
  loadedAt = 0;
  inflight = null;
  hydrated = false;
  loaded = false;
  publicClient = null;
}
